import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { authClient } from "@/api/auth-client";
import { expenseAdminTokenSource } from "@/auth";
import { Button } from "@/components/base/buttons/button";
import { ValidatedInput } from "@/components/base/input/validated-input";
import { buildCognitoAuthorizeUrl } from "@/utils/cognito-sso";
import { FormProvider, schemas, createFormSchema, useValidatedForm } from "@/utils/validation";
import { setCredentials } from "@/store/auth.slice";
import { useAppDispatch } from "@/store";
import { consumeAuthRedirect } from "@/utils/auth-redirect";

const loginSchema = createFormSchema({
    email: schemas.email,
    password: schemas.password,
});

type LoginFormData = { email: string; password: string };
type LoginStep = "email" | "password";

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const form = useValidatedForm(loginSchema, {
        defaultValues: { email: "", password: "" },
    });

    const notice = (location.state as { message?: string } | null)?.message ?? null;

    const [step, setStep] = useState<LoginStep>("email");
    const [isCheckingSso, setIsCheckingSso] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const handleContinue = async () => {
        const isEmailValid = await form.trigger("email");
        if (!isEmailValid) {
            return;
        }

        setAuthError(null);
        setIsCheckingSso(true);

        try {
            const ssoPayload: Parameters<typeof authClient.ssoCheck>[0] = {
                email: form.getValues("email"),
                siteName: import.meta.env.VITE_SITE_NAME?.trim() ?? "",
            };
            const ssoResponse = await authClient.ssoCheck(ssoPayload);

            if (ssoResponse.ssoEnabled && ssoResponse.identityProvider) {
                window.location.href = buildCognitoAuthorizeUrl(ssoResponse.identityProvider);
                return;
            }

            setStep("password");
        } catch {
            // If SSO check fails, allow users to continue with password login.
            setStep("password");
        } finally {
            setIsCheckingSso(false);
        }
    };

    const handlePasswordSubmit = async (data: LoginFormData) => {
        setIsLoggingIn(true);
        setAuthError(null);
        try {
            const loginPayload: Record<string, string> = {
                email: data.email,
                password: data.password,
            };
            const siteName = import.meta.env.VITE_SITE_NAME?.trim();

            if (siteName) {
                loginPayload.siteName = siteName;
            }

            const result = await authClient.login(loginPayload as unknown as Parameters<typeof authClient.login>[0]);

            // authClient.login() stores the access token via the local auth client.
            // Also update Redux so the app can use the access token immediately.
            dispatch(
                setCredentials({
                    accessToken: result.accessToken,
                }),
            );

            // Re-resolve permissions immediately so the guards show the loader
            // instead of flashing "access denied" during the poll interval.
            expenseAdminTokenSource.notifyTokenChange();

            navigate(consumeAuthRedirect() ?? "/", { replace: true });
        } catch (err) {
            const message = err instanceof Error ? err.message : "Login failed. Please try again.";
            setAuthError(message);
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <FormProvider form={form}>
            <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
                <div className="w-full max-w-md space-y-8">
                    {/* Logo/Header */}
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 text-white">
                                <span className="text-xl font-bold">ITS</span>
                            </div>
                        </div>
                        <h1 className="mt-6 text-3xl font-bold text-gray-900">Expense Admin</h1>
                        <p className="mt-2 text-sm text-gray-600">Sign in to your account</p>
                    </div>

                    {/* Notice (e.g. session ended due to missing permissions) */}
                    {notice && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" role="alert">
                            <p className="text-sm text-amber-800">{notice}</p>
                        </div>
                    )}

                    {/* Form */}
                    <form
                        noValidate
                        className="space-y-6"
                        onSubmit={(event) => {
                            event.preventDefault();

                            if (step === "email") {
                                void handleContinue();
                                return;
                            }

                            void form.handleSubmit(handlePasswordSubmit)(event);
                        }}
                    >
                        <ValidatedInput
                            form={form}
                            name="email"
                            label="Email address"
                            type="email"
                            placeholder="Enter your email"
                            autoComplete="email"
                            autoFocus
                            isRequired
                        />

                        {step === "password" && (
                            <ValidatedInput
                                form={form}
                                name="password"
                                label="Password"
                                type="password"
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                isRequired
                            />
                        )}

                        {step === "email" ? (
                            <Button type="submit" className="w-full" isLoading={isCheckingSso} isDisabled={isCheckingSso}>
                                Continue
                            </Button>
                        ) : (
                            <Button type="submit" className="w-full" isLoading={isLoggingIn} isDisabled={isLoggingIn}>
                                Sign in
                            </Button>
                        )}
                    </form>

                    {/* Error Message */}
                    {authError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-sm text-red-700">{authError}</p>
                        </div>
                    )}
                </div>
            </div>
        </FormProvider>
    );
}

LoginPage.displayName = "LoginPage";
